import { ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { Button, WorkoutsView, type UiTheme, type WorkoutsDiary } from "@heraion/ui";

import type { SessionsList } from "./useSessionsList";

// The Workouts list pane, split out of WorkoutsScreen.tsx (file-size cap,
// #368). On top List | Calendar (and Select on wide), then «New workout», or
// the selection bar in its place. The calendar's day goes under the grid
// here only when narrow: wide shows it in the detail pane.
export function WorkoutsListPane({
  theme,
  wide,
  diary,
  list,
  collapsedMonths,
  onToggleMonth,
  selectedId,
  onSelectSession,
  onNewWorkout,
  onRepeat,
  repeatingId,
  openingId,
}: {
  theme: UiTheme;
  wide: boolean;
  diary: WorkoutsDiary;
  list: SessionsList;
  collapsedMonths: ReadonlySet<string>;
  onToggleMonth: (key: string) => void;
  selectedId: string | null;
  onSelectSession: (id: string) => void;
  onNewWorkout: () => void;
  onRepeat: (id: string) => void;
  repeatingId: string | null;
  openingId: string | null;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.flex}>
      {diary.header}
      {diary.bar ?? (
        <View style={[styles.newWorkoutRow, { borderBottomColor: theme.palette.border }]}>
          <Button
            theme={theme}
            icon="plus"
            label={t("createHub.newWorkout.title", { defaultValue: "New workout" })}
            onPress={onNewWorkout}
            full
          />
        </View>
      )}
      {diary.mode === "calendar" ? (
        <ScrollView style={styles.flex}>
          {diary.calendar}
          {wide ? null : diary.dayList}
        </ScrollView>
      ) : (
        <WorkoutsView
          theme={theme}
          error={list.error}
          refreshing={list.refreshing}
          loadingMore={list.loadingMore}
          cursor={list.cursor}
          collapsedMonths={collapsedMonths}
          onToggleMonth={onToggleMonth}
          onRefresh={list.onRefresh}
          onEndReached={list.onEndReached}
          onSelectSession={onSelectSession}
          selectable
          selectedId={selectedId}
          enableRefreshControl={false}
          onRepeat={onRepeat}
          repeatingId={repeatingId}
          openingId={openingId}
          {...diary.listProps}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  newWorkoutRow: { padding: 12, borderBottomWidth: StyleSheet.hairlineWidth },
});
